import { useEffect, useRef, useState } from "react";

export type WorkshopEvent = {
  id: string;
  naam: string;
  datum: string;
  status: "Open voor inschrijving" | "Bijna vol" | "Vol";
  workshop: { naam: string; niveau?: string; duur?: string; prijs?: number };
  locatie: { naam: string; adres?: string };
  lumaUrl: string;
  plaatsenVrij: number;
  isAlmostFull: boolean;
  isFull: boolean;
};

type State =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ok"; events: WorkshopEvent[] };

const ENDPOINT = import.meta.env.VITE_WORKSHOPS_API_URL ?? "";
const REVALIDATE_INTERVAL_MS = 60_000;

export function useUpcomingWorkshops(): State {
  const [state, setState] = useState<State>({ status: "loading" });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!ENDPOINT) {
      setState({ status: "ok", events: [] });
      return;
    }

    const fetchEvents = async () => {
      try {
        // Cache-bust query so the browser HTTP cache doesn't serve a stale 60s response.
        const url = `${ENDPOINT}?t=${Date.now()}`;
        const res = await fetch(url, {
          headers: { Accept: "application/json" },
          cache: "no-store",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as { events: WorkshopEvent[] };
        if (mountedRef.current) {
          setState({ status: "ok", events: json.events ?? [] });
        }
      } catch {
        if (mountedRef.current) setState({ status: "error" });
      }
    };

    fetchEvents();

    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchEvents();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", fetchEvents);
    const intervalId = window.setInterval(fetchEvents, REVALIDATE_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", fetchEvents);
      window.clearInterval(intervalId);
    };
  }, []);

  return state;
}
